import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';

@Directive({
  selector: '[fGridDropdownCloser]',
  standalone: true
})
export class GridDropdownCloserDirective implements AfterViewInit, OnDestroy {
  private scrollHandler: ((event: Event) => void) | null = null;
  private gridContent: Element | null = null;
  private gridHeader: Element | null = null;

  constructor(private elementRef: ElementRef, private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.setupScrollListener();
  }

  ngOnDestroy(): void {
    this.removeEventListeners();
  }

  private setupScrollListener(): void {
    this.gridContent = this.elementRef.nativeElement.querySelector('.k-grid-content');
    this.gridHeader = this.elementRef.nativeElement.querySelector('.k-grid-header');

    if (this.gridContent) {
      this.scrollHandler = (): void => {
        this.manageDropdownVisibility();
      };

      this.ngZone.runOutsideAngular((): void => {
        this.gridContent!.addEventListener('scroll', this.scrollHandler!, { passive: true });
      });
    }
  }

  private manageDropdownVisibility(): void {
    if (!this.gridContent) return;

    const gridRect: DOMRect = this.gridContent.getBoundingClientRect();
    const headerRect: DOMRect | undefined = this.gridHeader?.getBoundingClientRect();
    const openDropdowns: NodeListOf<Element> = document.querySelectorAll('.dropdown-menu.show');

    openDropdowns.forEach((dropdown: Element): void => {
      this.handleDropdownVisibility(dropdown as HTMLElement, gridRect, headerRect);
    });
  }

  private handleDropdownVisibility(dropdown: HTMLElement, gridRect: DOMRect, headerRect?: DOMRect): void {
    const parentDropdown: Element | null = dropdown.closest('.dropdown');
    const triggerButton: HTMLElement | null = parentDropdown?.querySelector(
      'button, [data-automation-id="f-dropdown-menu"]'
    ) as HTMLElement;

    if (!triggerButton) return;

    const triggerRect: DOMRect = triggerButton.getBoundingClientRect();
    const viewportStatus: 'outside' | 'in-header' | 'in-content' = this.getViewportStatus(
      triggerRect,
      gridRect,
      headerRect
    );

    if (viewportStatus === 'outside') {
      this.hideDropdown(dropdown);
    } else if (viewportStatus === 'in-header') {
      this.applyHeaderOpacity(dropdown);
    } else {
      this.showDropdown(dropdown);
    }
  }

  private getViewportStatus(
    elementRect: DOMRect,
    gridRect: DOMRect,
    headerRect?: DOMRect
  ): 'outside' | 'in-header' | 'in-content' {
    const isOutsideGrid: boolean =
      elementRect.bottom < gridRect.top ||
      elementRect.top > gridRect.bottom ||
      elementRect.right < gridRect.left ||
      elementRect.left > gridRect.right;

    if (isOutsideGrid) {
      return 'outside';
    }

    if (headerRect) {
      const overlapsWithHeader: boolean =
        elementRect.top < headerRect.bottom &&
        elementRect.bottom > headerRect.top &&
        elementRect.left < headerRect.right &&
        elementRect.right > headerRect.left;

      if (overlapsWithHeader) {
        return 'in-header';
      }
    }

    return 'in-content';
  }

  private hideDropdown(dropdown: HTMLElement): void {
    dropdown.style.visibility = 'hidden';
    dropdown.style.opacity = '0';
    dropdown.style.pointerEvents = 'none';

    dropdown.setAttribute('data-hidden-by-scroll', 'true');
    dropdown.removeAttribute('data-header-opacity');
  }

  private applyHeaderOpacity(dropdown: HTMLElement): void {
    dropdown.style.visibility = 'visible';
    dropdown.style.opacity = '0';
    dropdown.style.pointerEvents = 'none';

    dropdown.setAttribute('data-header-opacity', 'true');
    dropdown.removeAttribute('data-hidden-by-scroll');
  }

  private showDropdown(dropdown: HTMLElement): void {
    if (
      dropdown.getAttribute('data-hidden-by-scroll') === 'true' ||
      dropdown.getAttribute('data-header-opacity') === 'true'
    ) {
      dropdown.style.visibility = 'visible';
      dropdown.style.opacity = '1';
      dropdown.style.pointerEvents = 'auto';

      dropdown.removeAttribute('data-hidden-by-scroll');
      dropdown.removeAttribute('data-header-opacity');
    }
  }

  private removeEventListeners(): void {
    if (this.gridContent) {
      if (this.scrollHandler) {
        this.gridContent.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
      this.gridContent = null;
    }
  }
}
